/**
 * The product-card call-to-action label — one rule, one module (FRIDGE-429, FRIDGE-814).
 *
 * Owner ruling: a product card's CTA reads **"Get Deal" when the card shows a
 * price** and **"See Deal" when it does not**. It binds every product card
 * sitewide — home carousels, collection grids, blog sidebars and inline cards,
 * hub grids, block rails, and the cards on a product page UNDERNEATH the hero.
 *
 * TWO THINGS THIS MODULE DELIBERATELY DOES NOT COVER:
 *
 *  1. The PDP hero product's own button. `app/products/[slug]/product-client.tsx`
 *     lines 1524-1535 (the Current Offers buy-box row, "View"/"Check") is the
 *     hero product button and is explicitly excluded by the ruling, along with
 *     the repeats of that same button elsewhere on the page (the two sticky buy
 *     bars, the reviews-inline / reviews-lightbox strips, and the verdict payoff
 *     CTA) — all of them clickouts for the page's OWN product, not cards.
 *
 *  2. Wayfinding links that name their destination — "View on Fridge.com →" on
 *     /deals, "View Filter" on the compatible-filter card. Those are not deal
 *     CTAs; renaming them would hide where the click goes.
 *
 * THE PRICE TEST HAS TO MATCH WHAT THE CARD ACTUALLY RENDERS. Every call site
 * binds ONE boolean, uses it to gate the price element, and passes the SAME
 * boolean to `dealCtaLabel`. Never derive the label from a second field that
 * merely correlates with the price (an offer id, a `lowest_price_cents` column
 * the card doesn't display, a discount flag) — that is exactly how a card ends
 * up promising a deal above an empty price row.
 *
 * Guarded by tests/product-card-deal-cta.test.ts.
 */

export const GET_DEAL_LABEL = 'Get Deal'
export const SEE_DEAL_LABEL = 'See Deal'

export type DealCtaLabel = typeof GET_DEAL_LABEL | typeof SEE_DEAL_LABEL

/**
 * The value a card's price element renders from, in any of the shapes the
 * cards actually hold it in: an already-formatted string ("$1,299"), a raw
 * number (dollars or cents — only the sign matters), or the boolean a card has
 * already computed for its own price gate.
 */
export type RenderedPrice = string | number | boolean | null | undefined

/**
 * Does the card SHOW a price?
 *
 * - boolean  → taken as the card's own already-computed price gate.
 * - number   → finite and greater than zero (0, NaN and negatives show nothing).
 * - string   → is a positive numeric amount, optionally formatted as a dollar price. "$1,299" and "From $89.99"
 *              are prices; "$0", "$0.00", "N/A", "Check current price" and ""
 *              are not.
 * - nullish  → no price.
 */
export function hasRenderedPrice(price: RenderedPrice): boolean {
  if (typeof price === 'boolean') return price
  if (price === null || price === undefined) return false
  if (typeof price === 'number') return Number.isFinite(price) && price > 0

  // Accept the price formats these cards display; model numbers and negative
  // or malformed amounts must never become a price by stripping their text.
  const match = /^(?:from\s+)?\$?\s*((?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d{1,2})?)$/i.exec(price.trim())
  if (!match) return false
  const amount = Number(match[1].replace(/,/g, ''))
  return Number.isFinite(amount) && amount > 0
}

/**
 * The card's CTA label. Pass the SAME value the card's price element renders
 * from — ideally the one boolean that gates that element.
 *
 * @example dealCtaLabel(showsPrice)   // the card's own price gate
 * @example dealCtaLabel('$1,299')     // → "Get Deal"
 * @example dealCtaLabel(null)         // → "See Deal"
 */
export function dealCtaLabel(price: RenderedPrice): DealCtaLabel {
  return hasRenderedPrice(price) ? GET_DEAL_LABEL : SEE_DEAL_LABEL
}
